import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@glplus/glpweb-dsl';
import { runnerApi } from '../lib/runnerApiClient';
import {
  bookedDaySet, deliveriesInRange, monthGrid, monthTitle, rangeDays, shiftMonth, todayYmd, weekdayInitials, ymdOf,
} from '../lib/calendar';

/**
 * The time-off day picker (Figma "Desktop — Pick days", 2026-09-12).
 *
 * 🔑 THE DOTS ARE THE POINT. Two `<input type="date">` fields let a Runner
 * choose a day that already has three deliveries on it and only find out when
 * the save is refused. This grid marks every day that already carries
 * deliveries, and totals them under the chosen range, so the choice is made
 * with the consequence visible rather than after it.
 *
 * 🪤 The month is fetched as it is opened — one small read per month, not a
 * year up front. A failed fetch loses the dots and nothing else: a picker that
 * refuses to open because a decoration did not load is the worse failure.
 */
export function DayPicker({
  startsOn,
  endsOn,
  disabled,
  onChange,
}: {
  startsOn: string;
  endsOn: string;
  disabled?: boolean;
  onChange: (startsOn: string, endsOn: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const today = todayYmd();
  const anchor = startsOn || today;

  const [{ year, month }, setMonth] = useState({
    year: Number(anchor.slice(0, 4)),
    month: Number(anchor.slice(5, 7)),
  });
  const [days, setDays] = useState<{ date: string; deliveries: number }[]>([]);

  useEffect(() => {
    const from = ymdOf(new Date(Date.UTC(year, month - 1, 1)));
    const to = ymdOf(new Date(Date.UTC(year, month, 0)));
    let live = true;
    void runnerApi
      .timeOffCalendar(from, to)
      .then((c) => { if (live) setDays(c.days); })
      .catch(() => { if (live) setDays([]); });
    return () => { live = false; };
  }, [year, month]);

  const weeks = useMemo(() => monthGrid(year, month), [year, month]);
  const booked = useMemo(() => bookedDaySet(days), [days]);
  const chosen = useMemo(
    () => (startsOn ? new Set(rangeDays(startsOn, endsOn || startsOn)) : new Set<string>()),
    [startsOn, endsOn],
  );
  const count = startsOn ? deliveriesInRange(days, startsOn, endsOn || startsOn) : 0;

  // Dates read to the Runner in their own language; the YYYY-MM-DD form stays
  // internal. Anchored at UTC for the same reason the grid is.
  const pretty = (ymd: string) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
      .format(new Date(`${ymd}T00:00:00.000Z`));
  const rangeLabel = !startsOn
    ? ''
    : !endsOn || endsOn === startsOn
      ? pretty(startsOn)
      : `${pretty(startsOn)} – ${pretty(endsOn)}`;

  /** First click opens a new range; the second closes it, in either order. */
  function pick(date: string) {
    if (!startsOn || (startsOn && endsOn && startsOn !== endsOn)) {
      onChange(date, date);
      return;
    }
    const [a, b] = date < startsOn ? [date, startsOn] : [startsOn, date];
    onChange(a, b);
  }

  return (
    <Box data-testid="timeoff-daypicker" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, maxWidth: 420 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Button
          ariaLabel={t('timeOff.picker.prev')}
          dataTestId="timeoff-picker-prev"
          buttonText="‹"
          variant="text"
          size="small"
          disabled={disabled}
          onClick={() => setMonth(shiftMonth(year, month, -1))}
        />
        <Typography data-testid="timeoff-picker-month" data-year-month={`${year}-${String(month).padStart(2, '0')}`} sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>
          {monthTitle(year, month, locale)}
        </Typography>
        <Button
          ariaLabel={t('timeOff.picker.next')}
          dataTestId="timeoff-picker-next"
          buttonText="›"
          variant="text"
          size="small"
          disabled={disabled}
          onClick={() => setMonth(shiftMonth(year, month, 1))}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {weekdayInitials(locale).map((d, i) => (
          <Typography
            key={`${d}-${i}`}
            aria-hidden
            sx={{ fontSize: '11px', color: '#8A93A6', textAlign: 'center', fontWeight: 700 }}
          >
            {d}
          </Typography>
        ))}
        {weeks.flat().map((cell, i) => {
          if (!cell.date) return <Box key={`blank-${i}`} />;
          const past = cell.date < today;
          const on = chosen.has(cell.date);
          const hasRuns = booked.has(cell.date);
          return (
            <Box
              key={cell.date}
              data-testid={`timeoff-day-${cell.date}`}
              data-has-deliveries={hasRuns ? 'true' : 'false'}
              component="button"
              type="button"
              disabled={disabled || past}
              aria-pressed={on}
              aria-label={`${pretty(cell.date)}${hasRuns ? `, ${t('timeOff.picker.hasDeliveries')}` : ''}`}
              onClick={() => pick(cell.date as string)}
              sx={{
                // 44px keeps the target at the accessible minimum even though
                // the visible cell is smaller than the column it sits in.
                minHeight: 40,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                border: 'none',
                borderRadius: 1.5,
                cursor: past || disabled ? 'default' : 'pointer',
                backgroundColor: on ? '#343F5B' : 'transparent',
                color: on ? '#FFFFFF' : past ? '#B7BECC' : '#1f2937',
                fontWeight: on ? 700 : 400,
                fontSize: '14px',
                fontFamily: 'inherit',
                '&:hover': { backgroundColor: on ? '#343F5B' : past || disabled ? 'transparent' : '#F4F6FA' },
              }}
            >
              {cell.day}
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  backgroundColor: hasRuns ? (on ? '#FFFFFF' : '#F67A1A') : 'transparent',
                }}
              />
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#F67A1A' }} />
        <Typography sx={{ fontSize: '12px', color: '#5f6b7a' }}>{t('timeOff.picker.legend')}</Typography>
      </Box>
      <Typography data-testid="timeoff-picker-chosen" data-deliveries={count} sx={{ fontSize: '12px', color: '#5f6b7a', mt: 0.5 }}>
        {startsOn
          ? t('timeOff.picker.chosen', { count, range: rangeLabel })
          : t('timeOff.picker.prompt')}
      </Typography>
    </Box>
  );
}
